/**
 * Public canvas share API.
 */
import apiClient, { createAuthConfig } from "../client";

/**
 * Create a share link from editor canvas composite (auth required).
 * @param {string} token
 * @param {Blob|File} file
 * @param {{ format?: string, export_size?: string, file_name?: string }} [options]
 */
export const createShareFromFile = async (token, file, options = {}) => {
  const formData = new FormData();
  formData.append("file", file, "composite.png");
  const format = options.format || "webp";
  formData.append("format", format);
  if (options.export_size) {
    formData.append("export_size", options.export_size);
  }
  if (options.file_name) {
    formData.append("file_name", options.file_name);
  }

  const auth = createAuthConfig(token);
  const response = await apiClient.post("/api/export/share", formData, {
    ...auth,
    timeout: 120000,
    headers: {
      ...auth.headers,
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

/**
 * Resolve public share metadata (no auth).
 * @param {string} shareId
 */
export const getSharedImage = async (shareId) => {
  const response = await apiClient.get(`/api/share/${encodeURIComponent(shareId)}`);
  return response.data;
};

/**
 * Copy text using the legacy execCommand fallback.
 * Works in more contexts than the async Clipboard API (which requires
 * transient user activation that can expire after a slow network call).
 * @param {string} text
 * @returns {boolean} whether the copy succeeded
 */
const copyViaExecCommand = (text) => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  let succeeded = false;
  try {
    succeeded = document.execCommand("copy");
  } catch {
    succeeded = false;
  }
  document.body.removeChild(textarea);
  return succeeded;
};

/**
 * Copy text to clipboard, falling back to execCommand when the async
 * Clipboard API is unavailable or rejected (e.g. lost user activation).
 * @param {string} text
 * @returns {Promise<boolean>} whether the copy succeeded
 */
export const copyTextToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Activation may have expired or permission denied; try the fallback.
    }
  }
  return copyViaExecCommand(text);
};

/**
 * @param {unknown} err
 * @returns {string}
 */
export const getShareErrorMessage = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return err?.message || "Share failed. Please try again.";
};

const shareApi = {
  createShareFromFile,
  getSharedImage,
  copyTextToClipboard,
  getShareErrorMessage,
};

export default shareApi;
